import { useEffect, useMemo, useState, useCallback } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  RefreshCw, Search, FileDown, ScrollText, Building2,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "pendente", label: "Pendente" },
  { key: "parcial", label: "Parcial" },
  { key: "quitado", label: "Quitado" },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

function today() { return new Date().toISOString().slice(0, 10); }
function firstDayOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastDayOfMonth() {
  const d = new Date(); const l = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${l.getFullYear()}-${String(l.getMonth() + 1).padStart(2, "0")}-${String(l.getDate()).padStart(2, "0")}`;
}

function StatusBadge({ status }) {
  const cls = {
    pendente: "bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25",
    parcial: "bg-[#FFF4D6] text-[#8B6A1F] border border-[#8B6A1F]/25",
    quitado: "bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20",
  }[status] || "bg-stone-100 text-stone-700 border border-stone-300";
  return (
    <span data-testid={`extrato-status-${status}`}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${cls}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function Kpi({ label, valor, sub, tone = "neutral", testid }) {
  const cls = {
    danger: "bg-[#F5E4DD] text-[#7c3a20] border-[#D4A652]/25",
    warn: "bg-[#FFF4D6] text-[#8B6A1F] border-[#8B6A1F]/25",
    ok: "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20",
    info: "bg-[#EDE9FE] text-[#5B21B6] border-[#5B21B6]/25",
    neutral: "bg-[#FFFFFF] text-stone-800 border-stone-200",
  }[tone];
  return (
    <div data-testid={testid} className={`border rounded-lg px-4 py-3 ${cls}`}>
      <div className="text-[11px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-serif text-xl tabular-nums">{brl(valor)}</div>
      {sub && <div className="text-[11px] opacity-75 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ExtratoConsolidado() {
  const [pousadas, setPousadas] = useState([]);
  const [filtros, setFiltros] = useState({
    data_inicio: firstDayOfMonth(),
    data_fim: lastDayOfMonth(),
    pousada_id: "all",
    status_pagamento: "all",
    busca: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const po = await api.get("/pousadas-list").catch(() => ({ data: [] }));
        setPousadas(Array.isArray(po.data) ? po.data.filter((x) => x.id) : []);
      } catch { /* silencioso */ }
    })();
  }, []);

  const pousadaMap = useMemo(() => Object.fromEntries(pousadas.map((p) => [p.id, p.nome])), [pousadas]);

  const buildParams = useCallback(() => {
    const p = {};
    if (filtros.data_inicio) p.data_inicio = filtros.data_inicio;
    if (filtros.data_fim) p.data_fim = filtros.data_fim;
    if (filtros.pousada_id !== "all") p.pousada_id = filtros.pousada_id;
    if (filtros.status_pagamento !== "all") p.status_pagamento = filtros.status_pagamento;
    if (filtros.busca.trim()) p.busca = filtros.busca.trim();
    return p;
  }, [filtros]);

  const load = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const { data: d } = await api.get("/extrato-consolidado", { params: buildParams() });
      setData(d);
    } catch (err) {
      setData(null);
      setErro(err?.response?.status === 401
        ? "Sessão expirada. Faça login novamente."
        : "Não foi possível carregar o extrato.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const exportarPdf = async () => {
    setExportando(true);
    try {
      const resp = await api.get("/extrato-consolidado/pdf", {
        params: buildParams(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const nome = `extrato-consolidado_${filtros.data_inicio || "inicio"}_${filtros.data_fim || "fim"}.pdf`;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF exportado");
    } catch (err) {
      const msg = err?.response?.status === 401 ? "Sessão expirada" : "Falha ao exportar PDF";
      toast.error(msg);
    } finally {
      setExportando(false);
    }
  };

  const itens = data?.itens || [];
  const totais = data?.totais || {};
  const porPousada = data?.por_pousada || [];
  const stCounts = data?.status_counts || {};

  return (
    <div data-testid="page-extrato-consolidado">
      <PageHeader
        eyebrow="Financeiro · Extrato"
        title="Extrato Consolidado"
        description="Visão oficial por período de movimentação (data de entrada). Recebido considera apenas pagamentos não estornados. Totais oficiais retornados pelo backend."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh-extrato">
              <RefreshCw size={14} /> Atualizar
            </Button>
            <Button size="sm" onClick={exportarPdf} disabled={exportando} data-testid="btn-exportar-pdf">
              <FileDown size={14} /> {exportando ? "Exportando..." : "Exportar PDF"}
            </Button>
          </div>
        }
      />

      {/* Filtros oficiais */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Entrada de</Label>
          <Input type="date" data-testid="filter-extrato-de" value={filtros.data_inicio}
            onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
          <Input type="date" data-testid="filter-extrato-ate" value={filtros.data_fim}
            onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Pousada</Label>
          <Select value={filtros.pousada_id} onValueChange={(v) => setFiltros({ ...filtros, pousada_id: v })}>
            <SelectTrigger data-testid="filter-extrato-pousada" className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status pagamento</Label>
          <Select value={filtros.status_pagamento} onValueChange={(v) => setFiltros({ ...filtros, status_pagamento: v })}>
            <SelectTrigger data-testid="filter-extrato-status" className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca (hóspede/reserva)</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={filtros.busca} data-testid="filter-extrato-busca"
              onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
              placeholder="Nome ou ID da reserva..." className="pl-8" />
          </div>
        </div>
      </div>

      {/* KPIs oficiais */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
          <Kpi testid="kpi-extrato-contratado" label="Contratado"
            valor={totais.contratado || 0} tone="neutral"
            sub={`${data.total_reservas || 0} reservas`} />
          <Kpi testid="kpi-extrato-recebido" label="Recebido"
            valor={totais.recebido || 0} tone="ok"
            sub="Pagamentos ativos" />
          <Kpi testid="kpi-extrato-estornado" label="Estornado"
            valor={totais.estornado || 0} tone="warn"
            sub="Pagamentos estornados" />
          <Kpi testid="kpi-extrato-pendente" label="Pendente"
            valor={totais.pendente || 0} tone="danger"
            sub="Contratado − Recebido" />
          <div data-testid="kpi-extrato-status-counts"
            className="border border-stone-200 rounded-lg px-4 py-3 bg-[#FFFFFF]">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Distribuição</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] uppercase text-[#083A6B]">Quitadas</div>
                <div className="font-serif text-lg tabular-nums">{stCounts.quitado || 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[#8B6A1F]">Parciais</div>
                <div className="font-serif text-lg tabular-nums">{stCounts.parcial || 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[#7c3a20]">Pendentes</div>
                <div className="font-serif text-lg tabular-nums">{stCounts.pendente || 0}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resumo por pousada */}
      {data && porPousada.length > 0 && (
        <div data-testid="extrato-por-pousada" className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto mb-6">
          <div className="px-4 py-2 border-b border-stone-200 flex items-center gap-2">
            <Building2 size={13} className="text-[#083A6B]" />
            <div className="text-xs uppercase tracking-widest text-stone-500">Resumo por pousada</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50 hover:bg-stone-50">
                <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Reservas</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Contratado</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Recebido</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Estornado</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Pendente</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {porPousada.map((p) => (
                <TableRow key={p.pousada_id || "sem_pousada"} data-testid={`extrato-pousada-${p.pousada_id || "sem_pousada"}`}>
                  <TableCell className="text-stone-900 text-sm">{p.pousada_nome || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.count}</TableCell>
                  <TableCell className="text-right tabular-nums">{brl(p.contratado)}</TableCell>
                  <TableCell className="text-right tabular-nums text-[#083A6B]">{brl(p.recebido)}</TableCell>
                  <TableCell className="text-right tabular-nums text-[#8B6A1F]">{brl(p.estornado)}</TableCell>
                  <TableCell className="text-right tabular-nums text-[#D4A652]">{brl(p.pendente)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Corpo — itens */}
      {loading ? (
        <div data-testid="extrato-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando extrato...
        </div>
      ) : erro ? (
        <div data-testid="extrato-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          {erro}
        </div>
      ) : itens.length === 0 ? (
        <div data-testid="extrato-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-4 text-[#083A6B]">
            <ScrollText size={22} strokeWidth={1.5} />
          </div>
          <div className="font-serif text-2xl text-stone-900 mb-2">Nenhum registro no período.</div>
          <p className="text-sm text-stone-600 max-w-md mx-auto">
            Ajuste o período ou os filtros. O extrato considera reservas pela data de entrada.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile */}
          <div className="md:hidden space-y-3">
            {itens.map((i) => (
              <div key={i.reserva_id} data-testid={`extrato-card-${i.reserva_id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-stone-900 truncate">{i.hospede_nome || "—"}</div>
                    <div className="text-xs text-stone-500">
                      {i.pousada_nome} · entrou em {fmtDate(i.data_entrada)}
                    </div>
                  </div>
                  <StatusBadge status={i.status_pagamento} />
                </div>
                <div className="text-xs text-stone-600 space-y-0.5 mb-1">
                  <div>Contratado: <b className="tabular-nums">{brl(i.contratado)}</b></div>
                  <div>Recebido: <b className="tabular-nums text-[#083A6B]">{brl(i.recebido)}</b></div>
                  {i.estornado > 0 && (
                    <div>Estornado: <b className="tabular-nums text-[#8B6A1F]">{brl(i.estornado)}</b></div>
                  )}
                  <div>Pendente: <b className="tabular-nums text-[#D4A652]">{brl(i.pendente)}</b></div>
                </div>
                <div className="text-[10px] text-stone-400 font-mono truncate">
                  {i.reserva_numero || i.reserva_id?.slice(0, 8)}…
                </div>
              </div>
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Entrada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Saída</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Hóspede</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Contratado</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Recebido</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Estornado</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Pendente</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((i) => (
                  <TableRow key={i.reserva_id} data-testid={`extrato-row-${i.reserva_id}`}>
                    <TableCell className="text-stone-700 text-sm whitespace-nowrap">{fmtDate(i.data_entrada)}</TableCell>
                    <TableCell className="text-stone-700 text-sm whitespace-nowrap">{fmtDate(i.data_saida)}</TableCell>
                    <TableCell className="text-stone-900 text-sm">
                      <div className="truncate max-w-[220px]">{i.hospede_nome || "—"}</div>
                      <div className="text-[10px] text-stone-400 font-mono">{i.reserva_numero || i.reserva_id?.slice(0, 8)}…</div>
                    </TableCell>
                    <TableCell className="text-stone-700 text-sm">
                      {i.pousada_nome || (i.pousada_id ? pousadaMap[i.pousada_id] : "—")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{brl(i.contratado)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#083A6B]">{brl(i.recebido)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#8B6A1F]">
                      {i.estornado > 0 ? brl(i.estornado) : <span className="text-stone-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-[#D4A652]">{brl(i.pendente)}</TableCell>
                    <TableCell><StatusBadge status={i.status_pagamento} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {data && (
        <div className="mt-4 text-[11px] text-stone-500">
          Regra oficial: filtro por <b>data_entrada</b>. Recebido considera apenas pagamentos <b>não</b> estornados. Estornado é apresentado como valor total dos pagamentos estornados (soft-cancel). Registros preservados pelo backend.
        </div>
      )}
    </div>
  );
}
